/**
 * SetupSheetDocument: the printable export (PRD §14.7, §9 — the literal
 * deliverable described in the original product brief: "150 Guests, 18
 * Round Tables, 2 Rectangle Tables, 1 DJ Booth, 1 Cake Table, 144 Chairs").
 *
 * Two parts, generated together from the same object graph so they can
 * never drift out of sync (PRD §9: "always in sync because they're
 * rendered from one data source"):
 *   1. The visual diagram (ReadOnlyLayoutView, numbered tables)
 *   2. The text summary block (counts by type)
 *
 * This component is print-target-aware: it's designed to be rendered
 * full-page via the browser's native print/PDF engine (see exportPdf.ts),
 * not screenshotted. Tailwind's print: variants control what's hidden
 * when actually printing vs viewed on-screen as a preview.
 */

import type { LayoutObject, ObjectCatalogEntry, RoomDefinition, ValidationIssue, Wedding } from '../../domain/types';
import { ReadOnlyLayoutView } from './ReadOnlyLayoutView';

interface SetupSheetDocumentProps {
  wedding: Wedding;
  room: RoomDefinition;
  objects: LayoutObject[];
  catalog: ObjectCatalogEntry[];
  chairStock?: number;
  /** Full, unfiltered issue list from validateLayout — this component
   * does its own severity filtering (see criticalWarnings/
   * meaningfulWarnings below), rather than receiving an already-filtered
   * list, so it can apply its own print-specific rule: "info"-severity
   * issues (e.g. two tables a few inches closer than ideal — extremely
   * common in real, workable layouts) are omitted from the printed page
   * entirely. There's no "tap to expand and see minor notes" affordance
   * on paper the way there is in the editor, and these are explicitly
   * non-actionable recommendations a coordinator chooses to inspect on
   * screen, not something a setup crew needs printed in front of them. */
  warnings?: ValidationIssue[];
  /** When false, suppresses the entire "Needs Attention" validation section
   * from the printed sheet. The coordinator controls this via the toggle in
   * SetupSheetPreview. Defaults to true so existing layouts are unchanged. */
  showWarningsOnSetupSheet?: boolean;
}

const TABLE_TYPES = ['round_table', 'rect_table_6', 'rect_table_8'];

const DISPLAY_ORDER = [
  'round_table',
  'rect_table_6',
  'rect_table_8',
  'head_table',
  'sweetheart_table',
  'dance_floor_15',
  'dance_floor_18',
  'dj_booth',
  'cake_table',
  'gift_table',
  'dessert_station',
  'donut_wall',
  'photo_booth',
  'cocktail_table',
];

export function SetupSheetDocument({ wedding, room, objects, catalog, warnings = [], showWarningsOnSetupSheet = true }: SetupSheetDocumentProps) {
  const totalSeats = objects.reduce((sum, o) => sum + (o.seats ?? 0), 0);
  const totalChairs = totalSeats; // chairs derived 1:1 from seats, never placed independently (PRD §6)
  const totalGuestTables = objects.filter((o) => TABLE_TYPES.includes(o.type)).length;
  const textNotes = objects.filter((o) => o.type === 'text_note' && o.label.trim().length > 0);
  // Info-tier issues are deliberately excluded here — see the warnings
  // prop's doc comment above for why a printed page handles this
  // differently from the editor's collapsible "Minor recommendations"
  // section.
  const criticalWarnings = warnings.filter((w) => w.severity === 'critical');
  const meaningfulWarnings = warnings.filter((w) => w.severity === 'warning');

  const countsByType = new Map<string, number>();
  for (const obj of objects) {
    countsByType.set(obj.type, (countsByType.get(obj.type) ?? 0) + 1);
  }

  const formattedDate = new Date(wedding.eventDate + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="mx-auto max-w-3xl bg-white p-8 print:p-0">
      <header className="border-b-2 border-gold-400 pb-4 text-center">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-ink-400">
          The Venue at Lilly Lou&rsquo;s
        </p>
        <h1 className="mt-1 font-display text-2xl text-ink-700">{room.displayName} Room</h1>
        <p className="mt-1 font-body text-sm text-ink-500">
          {wedding.coupleNames} {'\u00b7'} {formattedDate}
        </p>
      </header>

      <div className="mt-6 overflow-x-auto">
        <ReadOnlyLayoutView room={room} objects={objects} catalog={catalog} showNumbers />
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-center gap-4 font-body text-xs text-ink-500">
        <LegendItem swatchClass="rounded-full border border-sage-300 bg-white" label="Guest table" />
        <LegendItem swatchClass="border border-gold-500 bg-gold-200" label="Dance floor" />
        <LegendItem swatchClass="rounded border border-sage-300 bg-white" label="Feature zone" />
        <LegendItem swatchClass="border border-sage-400 bg-sage-100" label="Fixed feature" />
      </div>

      <section className="mt-8 rounded-lg border border-ivory-300 p-5">
        <h2 className="font-body text-xs uppercase tracking-wide text-ink-400">Setup Summary</h2>

        <p className="mt-2 font-display text-lg text-ink-700">
          {wedding.guestCount} Guests {'\u00b7'} {totalGuestTables} Tables {'\u00b7'} {totalChairs} Chairs
        </p>

        <ul className="mt-4 grid grid-cols-2 gap-y-1.5 font-body text-sm text-ink-600 sm:grid-cols-3">
          {DISPLAY_ORDER.filter((type) => countsByType.has(type)).map((type) => (
            <li key={type}>
              {countsByType.get(type)} {labelFor(type, catalog)}
            </li>
          ))}
        </ul>

        {showWarningsOnSetupSheet && (criticalWarnings.length > 0 || meaningfulWarnings.length > 0) && (
          <div className="mt-5 space-y-3">
            {criticalWarnings.length > 0 && (
              <div className="rounded border border-danger/40 bg-danger/5 p-3">
                <p className="font-body text-xs font-medium uppercase tracking-wide text-danger">
                  {'\u26A0'} Needs attention before setup
                </p>
                <ul className="mt-1.5 space-y-1 font-body text-xs text-ink-600">
                  {criticalWarnings.map((w) => (
                    <li key={w.id}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {meaningfulWarnings.length > 0 && (
              <div className="rounded border border-warning/40 bg-warning/5 p-3">
                <p className="font-body text-xs font-medium uppercase tracking-wide text-warning">
                  {'\u26A0'} Worth a look
                </p>
                <ul className="mt-1.5 space-y-1 font-body text-xs text-ink-600">
                  {meaningfulWarnings.map((w) => (
                    <li key={w.id}>{w.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {textNotes.length > 0 && (
          <div className="mt-5 rounded border border-ivory-300 bg-ivory-50 p-3">
            <p className="font-body text-xs font-medium uppercase tracking-wide text-ink-400">
              Notes on this layout
            </p>
            <ul className="mt-1.5 space-y-1 font-body text-sm italic text-ink-600">
              {textNotes.map((note) => (
                <li key={note.id}>&ldquo;{note.label}&rdquo;</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 border-t border-ivory-200 pt-3">
          <p className="font-body text-xs text-ink-400">Notes for setup crew:</p>
          <div className="mt-1 h-16 border border-dashed border-ivory-300" />
        </div>
      </section>

      <footer className="mt-6 text-center font-body text-xs text-ink-300">
        Generated by The Pines Planner
      </footer>
    </div>
  );
}

function labelFor(type: string, catalog: ObjectCatalogEntry[]): string {
  const entry = catalog.find((c) => c.itemType === type);
  return entry?.displayName ?? type;
}

function LegendItem({ swatchClass, label }: { swatchClass: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block h-3 w-3 ${swatchClass}`} aria-hidden />
      {label}
    </span>
  );
}
